import {
  InfiniteData,
  keepPreviousData,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import {
  api,
  ContactsListDto,
  QueryKey,
  ServiceCategoryDto,
  SpendCategoryListDto,
  CreateVendorDto,
  FileUploadDto,
  UpdateVendorDto,
  VectorContractsListDto,
  VendorCreateManyDto,
  VendorDetailDto,
  VendorListDto,
  VendorSelectListDto,
  VendorsListDto,
  VendorImportResultDto,
  VendorOrganizationSelectListDto,
  UpdateOrganizationDto,
  VendorClientListDto,
} from '@sib/api';
import { DEFAULT_PAGE } from '@sib/api/constants';

const CACHE_TIME = 5000;

export const useVendorsList = (
  organizationId: string,
  query: Parameters<
    typeof api.organizations.organizationVendorsControllerGetAllVendorsByOrganizationName
  >[1],
  options?: UseQueryOptions<VendorsListDto>
) =>
  useQuery({
    ...options,
    enabled: !!organizationId,
    queryKey: [QueryKey.Resource.Vendors, query, organizationId],
    gcTime: CACHE_TIME,
    staleTime: CACHE_TIME,
    queryFn: async () =>
      api.organizations.organizationVendorsControllerGetAllVendorsByOrganizationName(
        organizationId,
        query
      ),
  });

export const useGetVendorsSelectInfiniteList = (
  query: Parameters<
    typeof api.vendors.vendorsControllerGetAllVendorsSelectList
  >[0],
  options?: Partial<
    UseInfiniteQueryOptions<
      VendorSelectListDto,
      Error,
      InfiniteData<VendorSelectListDto>
    >
  >
) =>
  useInfiniteQuery({
    ...options,
    queryKey: [
      QueryKey.Resource.VendorsSelectList,
      QueryKey.Operation.List,
      query,
      QueryKey.Operation.Infinite,
      ...(Array.isArray(options?.queryKey) ? options.queryKey : []),
    ],
    queryFn: async ({ pageParam }) =>
      api.vendors.vendorsControllerGetAllVendorsSelectList({
        ...query,
        page: pageParam as number,
      }),
    //TODO : Fix the type of pageParam after backend type fix
    getNextPageParam: (lastPage: any) => {
      const hasNextPage = lastPage.hasNextPage;
      const nextPage = lastPage.page + 1;

      return hasNextPage ? nextPage : undefined;
    },
    initialPageParam: query?.page || DEFAULT_PAGE,
    placeholderData: keepPreviousData,
  });

export const useInternalPortalVendorsList = (
  query: Parameters<typeof api.internalPortal.vendorsControllerGetVendors>[0],
  options?: UseQueryOptions<VendorListDto>
) =>
  useQuery({
    queryKey: [
      QueryKey.Resource.InternalPortalVendors,
      QueryKey.Operation.List,
      query,
    ],
    queryFn: async () => api.internalPortal.vendorsControllerGetVendors(query),
    ...options,
  });

export const useInternalPortalUnassignedToOrganizationVendorsList = (
  organizationId: string,
  query: Parameters<
    typeof api.internalPortal.organizationRelationsControllerGetUnassignedOrganizationVendors
  >[1],
  options?: UseQueryOptions<VendorListDto>
) =>
  useQuery({
    queryKey: [
      QueryKey.Resource.InternalPortalUnassignedToOrganizationVendors,
      QueryKey.Operation.List,
      organizationId,
      query,
    ],
    queryFn: async () =>
      api.internalPortal.organizationRelationsControllerGetUnassignedOrganizationVendors(
        organizationId,
        query
      ),
    ...options,
  });

export const useInternalPortalVendorContactsList = (
  vendorId: string,
  query: NonNullable<
    Parameters<
      typeof api.internalPortal.vendorContactsControllerGetVendorContacts
    >[1]
  >,
  options?: UseQueryOptions<ContactsListDto>
) =>
  useQuery({
    ...options,
    enabled: !!vendorId && (options?.enabled ?? true),
    queryKey: [
      QueryKey.Resource.VendorContacts,
      QueryKey.Operation.List,
      vendorId,
      query,
    ],
    queryFn: async () =>
      api.internalPortal.vendorContactsControllerGetVendorContacts(
        vendorId,
        query
      ),
  });

export const useVendorServiceCategories = (
  organizationId: string,
  options?: UseQueryOptions<ServiceCategoryDto[], Error, ServiceCategoryDto[]>
) =>
  useQuery({
    ...options,
    enabled: !!organizationId,
    queryKey: [
      QueryKey.Resource.VendorsServiceCategories,
      QueryKey.Operation.List,
      organizationId,
    ],
    queryFn: async () =>
      api.organizations.organizationVendorsControllerGetAllVendorServiceCategories(
        organizationId
      ),
  });

export const useVecotorContracts = (
  organizationId: string,
  vendorId: string,
  query: Parameters<
    typeof api.organizations.vectorContractsControllerGetVectorContracts
  >[2],
  options?: UseQueryOptions<VectorContractsListDto>
) =>
  useQuery({
    ...options,
    enabled: !!organizationId && !!vendorId,
    queryKey: [
      QueryKey.Resource.VectorContracts,
      query,
      organizationId,
      vendorId,
    ],
    gcTime: CACHE_TIME,
    staleTime: CACHE_TIME,
    queryFn: async () =>
      api.organizations.vectorContractsControllerGetVectorContracts(
        organizationId,
        vendorId,
        query
      ),
  });

export const useInternalPortalVendorCreate = (
  options?: Partial<UseMutationOptions<VendorDetailDto, Error, CreateVendorDto>>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: [
          QueryKey.Resource.InternalPortalVendors,
          QueryKey.Operation.List,
        ],
      });
      if (variables.clientIds?.[0]) {
        queryClient.invalidateQueries({
          queryKey: [
            QueryKey.Resource.InternalPortalVendors,
            QueryKey.Operation.List,
            variables.clientIds?.[0],
          ],
        });
      }

      options?.onSuccess?.(data, variables, context);
    },
    mutationFn: async (data: CreateVendorDto) =>
      api.internalPortal.vendorsControllerCreateVendor(data),
  });
};

export const useInternalPortalVendorUpdate = (
  options?: Partial<UseMutationOptions<VendorDetailDto, Error, UpdateVendorDto>>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: [
          QueryKey.Resource.InternalPortalVendors,
          QueryKey.Operation.One,
          variables.vendorId,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: [
          QueryKey.Resource.InternalPortalVendors,
          QueryKey.Operation.List,
        ],
      });
      options?.onSuccess?.(data, variables, context);
    },
    mutationFn: async ({
      vendorId,
      ...data
    }: { vendorId: string } & UpdateVendorDto) =>
      api.internalPortal.vendorsControllerUpdateVendor(vendorId, data),
  });
};

export const useInternalPortalVendorGetById = (
  vendorId: string,
  options?: UseQueryOptions<VendorDetailDto>
) =>
  useQuery({
    ...options,
    enabled: !!vendorId,
    queryKey: [
      QueryKey.Resource.InternalPortalVendors,
      QueryKey.Operation.One,
      vendorId,
    ],
    gcTime: CACHE_TIME,
    staleTime: CACHE_TIME,
    queryFn: async () =>
      api.internalPortal.vendorsControllerGetVendorById(vendorId),
  });

export const useInternalPortalVendorSpendCategories = (
  query?: Parameters<
    typeof api.internalPortal.vendorsControllerListSpendCategories
  >[0],
  options?: UseQueryOptions<SpendCategoryListDto>
) =>
  useQuery({
    ...options,
    queryKey: [QueryKey.Resource.InternalPortalVendorSpendCategories, query],
    gcTime: CACHE_TIME,
    staleTime: CACHE_TIME,
    queryFn: async () =>
      api.internalPortal.vendorsControllerListSpendCategories(query),
  });

export const useInternalPortalInfiniteSpendCategories = (
  query?: Parameters<
    typeof api.internalPortal.vendorsControllerListSpendCategories
  >[0],
  options?: Partial<
    UseInfiniteQueryOptions<
      SpendCategoryListDto,
      Error,
      InfiniteData<SpendCategoryListDto>
    >
  >
) =>
  useInfiniteQuery({
    ...options,
    queryKey: [QueryKey.Resource.InternalPortalVendorSpendCategories, query],
    queryFn: async ({ pageParam }) =>
      api.internalPortal.vendorsControllerListSpendCategories({
        ...query,
        page: pageParam as number,
      }),
    getNextPageParam: lastPage =>
      lastPage.hasNextPage ? lastPage.page + 1 : undefined,
    initialPageParam: query?.page || DEFAULT_PAGE,
    placeholderData: keepPreviousData,
  });

export const useDownloadVendorsImportTemplate = (
  options?: Partial<UseMutationOptions<Blob, Error, void>>
) =>
  useMutation({
    ...options,
    mutationFn: async () =>
      api.internalPortal.vendorsControllerGetImportTemplate({
        format: 'blob',
      }) as unknown as Blob,
  });

export const useVendorsValidateImport = (
  options?: Partial<
    UseMutationOptions<VendorImportResultDto, Error, FileUploadDto>
  >
) =>
  useMutation({
    ...options,
    mutationFn: async (data: FileUploadDto) =>
      api.internalPortal.vendorsControllerValidateImport(data),
  });

export const useVendorsCreateMany = (
  options?: Partial<UseMutationOptions<void, Error, VendorCreateManyDto>>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: [
          QueryKey.Resource.InternalPortalVendors,
          QueryKey.Operation.List,
        ],
      });

      options?.onSuccess?.(data, variables, context);
    },
    mutationFn: async (data: VendorCreateManyDto) =>
      api.internalPortal.vendorsControllerCreateMany(data),
  });
};

export const useInfiniteInternalPortalVendorOrganizations = (
  vendorId: string,
  query: Parameters<
    typeof api.internalPortal.vendorsControllerGetVendorOrganizations
  >[1],
  options?: Partial<
    UseInfiniteQueryOptions<
      VendorOrganizationSelectListDto,
      Error,
      InfiniteData<VendorOrganizationSelectListDto>
    >
  >
) =>
  useInfiniteQuery({
    ...options,
    queryKey: [
      QueryKey.Resource.InternalPortalVendorOrganizations,
      QueryKey.Operation.List,
      query,
      vendorId,
      QueryKey.Operation.Infinite,
    ],
    queryFn: ({ pageParam }) =>
      api.internalPortal.vendorsControllerGetVendorOrganizations(vendorId, {
        ...query,
        page: pageParam as number,
      }),
    getNextPageParam: lastPage => {
      const hasNextPage = lastPage.hasNextPage;
      const nextPage = lastPage.page + 1;

      return hasNextPage ? nextPage : undefined;
    },
    initialPageParam: query?.page || DEFAULT_PAGE,
    placeholderData: keepPreviousData,
  });

export const useInternalPortalOrganizationVendors = (
  organizationId: string,
  query: Parameters<
    typeof api.internalPortal.organizationRelationsControllerGetOrganizationVendors
  >[1],
  options?: UseQueryOptions<VendorClientListDto>
) =>
  useQuery({
    ...options,
    enabled: !!organizationId,
    queryKey: [
      QueryKey.Resource.InternalPortalVendors,
      QueryKey.Operation.List,
      organizationId,
      query,
    ],
    gcTime: CACHE_TIME,
    staleTime: CACHE_TIME,
    queryFn: async () =>
      api.internalPortal.organizationRelationsControllerGetOrganizationVendors(
        organizationId,
        query
      ),
  });
